/**
 * Patrón de Ruptura Quantum — adapter de SOLO LECTURA sobre el motor legacy.
 *
 * CERO lógica de cálculo propia: traduce la config Quantum a la config del
 * motor (solo las claves que el motor conoce), llama a `detect` del motor y
 * mapea su salida al shape interno (types.js). El break-even y el timeout del
 * motor no tienen representación visual propia: el outcome se conserva crudo
 * y la presentación decide qué pintar (el TP3 sí se presenta desde v1.1).
 *
 * Nunca lanza excepción: cualquier entrada inválida devuelve la salida vacía.
 */

import { detect as engineDetect, defaultConfig as engineDefaultConfig } from './engine.js'

// ── Helpers de tiempo (copiados de choch_bos_quantum/index.js:10-18) ────────

function toSeconds(t) {
  if (t == null || t === '' || Number.isNaN(t)) return null
  return t > 1e10 ? Math.floor(t / 1000) : t
}

function cleanTime(t) {
  if (t == null || t === '' || Number.isNaN(t)) return null
  return toSeconds(t)
}

function emptyOutput() {
  return { patterns: [], signals: [], activeSignal: null, lastSignal: null, stats: null, patternInfo: null }
}

/**
 * Traduce la config Quantum resuelta a la config del motor: solo las claves
 * que el motor conoce (los toggles visuales/Quantum no cruzan la frontera).
 */
function toEngineConfig(cfg) {
  return {
    ...engineDefaultConfig,
    pivotLen: cfg.pivotLen,
    minTouches: cfg.minTouches,
    maxChannelBars: cfg.maxChannelBars,
    convergenceMin: cfg.convergenceMin,
    volConfirm: cfg.volConfirm,
    volContraction: cfg.volContraction,
    momentumFilter: cfg.momentumFilter,
    slPadding: cfg.slPadding,
    touchTolerance: cfg.touchTolerance,
    deviationMax: cfg.deviationMax,
    minChannelWidthAtr: cfg.minChannelWidthAtr,
  }
}

/** Resuelve el time de una vela por índice; null si no resuelve. */
function timeAt(candles, idx) {
  const i = Math.round(idx)
  if (!Number.isFinite(i) || i < 0 || i >= candles.length) return null
  return cleanTime(candles[i]?.time)
}

/**
 * Canal del motor → PrqPattern. Muestrea upperFn/lowerFn en el inicio y en el
 * fin (si el canal está roto, el fin se proyecta hasta la vela de ruptura —
 * los puntos llegan congelados al renderer). Si la serie temporal no resuelve
 * algún extremo, el canal se descarta: nunca se pinta con datos parciales.
 *
 * Además materializa `fill` (un punto {time, upper, lower} por vela del rango
 * [startIdx, fin proyectado]) para el relleno del canal, `bias` (dirección de
 * la ruptura o neutral) y `strength`:
 *
 * El motor NO guarda strength por canal — solo `convergence`/`volContracted`
 * y toques. El strength 0-100 vive en la SEÑAL (`signals[].strength`), y la
 * señal se emite en la misma vela en que el canal queda marcado como roto
 * (`brokenAt === signal.idx`, ver engine.js líneas 493-535).
 * Por eso el strength del patrón es el de su señal asociada (0-100) y null
 * si el canal no llegó a romper. `strengthByIdx` es el mapa idx → strength
 * construido en runAdapter.
 */
function mapChannel(ch, candles, strengthByIdx) {
  if (!ch || typeof ch.upperFn !== 'function' || typeof ch.lowerFn !== 'function') return null
  if (!Number.isFinite(ch.startIdx) || !Number.isFinite(ch.endIdx)) return null

  const broken = (ch.brokenDir === 1 || ch.brokenDir === -1) && Number.isFinite(ch.brokenAt)
  const endIdx = broken ? ch.brokenAt : ch.endIdx

  const startTime = timeAt(candles, ch.startIdx)
  const endTime = timeAt(candles, endIdx)
  if (startTime == null || endTime == null) return null

  const points = []
  for (const idx of [ch.startIdx, endIdx]) {
    const time = timeAt(candles, idx)
    const upper = ch.upperFn(idx)
    const lower = ch.lowerFn(idx)
    if (time == null || !Number.isFinite(upper) || !Number.isFinite(lower)) return null
    points.push({ time, upper, lower })
  }

  // Serie de relleno: un punto por vela del rango. Los índices del motor son
  // enteros sobre el array de velas (startIdx = Math.floor(startX), endIdx =
  // vela de detección o de ruptura), así que el barrido es directo.
  const fill = []
  for (let idx = Math.ceil(ch.startIdx); idx <= endIdx; idx++) {
    const time = timeAt(candles, idx)
    const upper = ch.upperFn(idx)
    const lower = ch.lowerFn(idx)
    if (time == null || !Number.isFinite(upper) || !Number.isFinite(lower)) continue
    fill.push({ time, upper, lower })
  }

  const brokenDir = ch.brokenDir === 1 ? 1 : ch.brokenDir === -1 ? -1 : null
  const strength = broken ? strengthByIdx?.get(ch.brokenAt) : null

  return {
    id: `prq:ch:${startTime}:${endTime}`,
    startTime,
    endTime,
    resistance: points.map(p => ({ time: p.time, price: p.upper })),
    support: points.map(p => ({ time: p.time, price: p.lower })),
    brokenDir,
    brokenAt: broken ? ch.brokenAt : null,
    fill,
    strength: Number.isFinite(strength) ? strength : null,
    bias: brokenDir === 1 ? 'bull' : brokenDir === -1 ? 'bear' : 'neutral',
  }
}

/** Señal del motor → PrqSignal (misma traducción para historial y activa). */
function mapSignal(sig, context) {
  if (!sig || sig.dir !== 1 && sig.dir !== -1) return null
  if (!Number.isFinite(sig.entry) || !Number.isFinite(sig.sl) || !Number.isFinite(sig.tp1) || !Number.isFinite(sig.tp2)) return null
  const time = cleanTime(sig.time)
  if (time == null) return null

  const tp1Hit = !!sig.tp1Hit
  const tp2Hit = !!sig.tp2Hit
  const tp3Hit = !!sig.tp3Hit
  const outcome = sig.result === 'win' || sig.result === 'loss' || sig.result === 'timeout'
    ? sig.result
    : 'active'
  // state: mientras está viva refleja el progreso (OPEN/TP1/TP2); al cerrar,
  // 'win' se presenta como TP3 (semántica real del motor: result='win' es
  // tp3Hit o salida en break-even — se distinguen por tp3Hit/pnl, ver
  // types.js) y cualquier otro cierre (loss/timeout) como SL. El outcome
  // crudo sigue disponible para el detalle.
  const state = outcome === 'active'
    ? (tp2Hit ? 'TP2' : tp1Hit ? 'TP1' : 'OPEN')
    : (outcome === 'win' ? 'TP3' : 'SL')

  const symbol = typeof context?.symbol === 'string' && context.symbol ? context.symbol : 'na'
  const timeframe = typeof context?.timeframe === 'string' && context.timeframe ? context.timeframe : 'na'

  return {
    id: `prq:${symbol}:${timeframe}:${time}`,
    time,
    direction: sig.dir === 1 ? 'LONG' : 'SHORT',
    entry: sig.entry,
    sl: sig.sl,
    tp1: sig.tp1,
    tp2: sig.tp2,
    tp3: Number.isFinite(sig.tp3) ? sig.tp3 : null,
    state,
    outcome,
    tp1Hit,
    tp2Hit,
    tp3Hit,
    pnl: Number.isFinite(sig.pnl) ? sig.pnl : null,
    closeTime: cleanTime(sig.closeTime),
  }
}

/**
 * Ejecuta el motor sobre las velas y mapea su salida al shape Quantum.
 * @param {Array} candles  velas ya saneadas (OHLC numéricos)
 * @param {Object} cfg     config ya resuelta por resolveConfig()
 * @param {{symbol?: string|null, timeframe?: string|null}} context
 */
export function runAdapter(candles, cfg, context) {
  try {
    if (!Array.isArray(candles) || candles.length === 0 || !cfg) return emptyOutput()
    const result = engineDetect(candles, toEngineConfig(cfg))
    if (!result || typeof result !== 'object') return emptyOutput()

    // Mapa idx de ruptura → strength de la señal (ver mapChannel): es la
    // única fuente de fuerza por canal que expone el motor.
    const engineSignals = Array.isArray(result.signals) ? result.signals : []
    const strengthByIdx = new Map()
    for (const s of engineSignals) {
      if (Number.isFinite(s?.idx) && Number.isFinite(s?.strength)) strengthByIdx.set(s.idx, s.strength)
    }

    const patterns = (Array.isArray(result.allChannels) ? result.allChannels : [])
      .map(ch => mapChannel(ch, candles, strengthByIdx))
      .filter(Boolean)

    const signals = engineSignals
      .map(s => mapSignal(s, context))
      .filter(Boolean)

    const activeSignal = result.activeTrade ? mapSignal({ ...result.activeTrade, result: 'active', pnl: null }, context) : null
    const lastSignal = result.lastSignal ? mapSignal(result.lastSignal, context) : null

    // Dashboard crudo del motor (pattern/strength/bullProb/rr/touches/
    // volContraction/convergence/trade) para la sección "Patrón activo" del
    // badge. Se pasa tal cual: la presentación decide cómo mostrarlo.
    const patternInfo = result.dashboard && typeof result.dashboard === 'object' ? result.dashboard : null

    return { patterns, signals, activeSignal, lastSignal, stats: null, patternInfo }
  } catch {
    return emptyOutput()
  }
}
